import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EnhancedPartner, PartnerStatus, PartnerFilters, ProvisioningStatus } from '../../types/partner';
import { Task } from '../../types/task';
import { ResourceStatusBadge } from '../shared/StatusBadge';
import { Button } from '../shared/Button';
import { usePartnerData } from '../../hooks/usePartnerData';
import { useTaskManagement } from '../../hooks/useTaskManagement';
import { useConnectionData } from '../../hooks/useConnectionData';
import PartnerDetails from './PartnerDetails';
import CreatePartner from '../CreatePartner';
import { RoleGuard } from '../RoleGuard';
// THUB-186: shared timezone-aware formatters; `sharedRelativeTime` is aliased so the
// local wrapper below can keep its name and richer {text, secondary} return shape.
import {
  formatDateTime,
  formatRelativeTime as sharedRelativeTime,
} from '../../utils/formatDateTime';
import { useTimezone } from '../../contexts/TimezoneContext';

interface PartnerListProps {
  showTaskCounts?: boolean;
  showConnectionStatus?: boolean;
  groupBy?: 'status' | 'type' | 'activity';
  onPartnerSelect?: (partner: EnhancedPartner) => void;
}

function PartnerList({
  showTaskCounts = true,
  showConnectionStatus = true,
  groupBy: _groupBy = 'status',
  onPartnerSelect
}: PartnerListProps) {
  const { partnerId } = useParams<{ partnerId?: string }>();
  const navigate = useNavigate();
  // THUB-186: subscribe so a timezone change re-renders these timestamps.
  const { timezone } = useTimezone();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);


  // Filters
  const [filters, setFilters] = useState<PartnerFilters>({
    status: [],
    isActive: [],
    hasActiveConnections: [],
    hasActiveTasks: []
  });

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Sorting
  const [sortField, setSortField] = useState<keyof EnhancedPartner>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const {
    partners: basePartners,
    loading: partnersLoading,
    error: partnersError,
    connectionStatuses,
    refreshPartners
  } = usePartnerData({ autoSubscribe: false });

  const {
    tasks,
    loading: tasksLoading,
    getTasksByPartner
  } = useTaskManagement({ autoSubscribe: false });

  const {
    connections: allConnections,
    loading: connectionsLoading,
    getConnectionsByPartner
  } = useConnectionData({ autoSubscribe: false });

  // Enhanced partners with task information and real-time status
  const partners: EnhancedPartner[] = useMemo(() => {
    return basePartners.map(partner => {
      const partnerTasks = getTasksByPartner(partner.partnerId);
      const partnerConnections = getConnectionsByPartner(partner.partnerId);

      const taskCounts = {
        total: partnerTasks.length,
        enabled: partnerTasks.filter((t: Task) => t.status === 'enabled').length,
        disabled: partnerTasks.filter((t: Task) => t.status === 'disabled').length,
        running: partnerTasks.filter((t: Task) => t.lastExecution?.status === 'running').length,
      };

      // Use API status, only override if we have active task information
      const status: PartnerStatus = taskCounts.running > 0 ? 'ready' :
        taskCounts.enabled > 0 ? 'idle' :
        taskCounts.total > 0 ? 'not_used' : partner.status;

      const inboundTasks = partnerTasks.filter(t => t.transferDirection === 'inbound');
      const outboundTasks = partnerTasks.filter(t => t.transferDirection === 'outbound');

      const getMetric = (task: Task, key: 'totalExecutions' | 'successfulExecutions' | 'failedExecutions' | 'totalFilesProcessed' | 'totalBytesTransferred' | 'averageExecutionTime'): number => {
        const value = task.metrics?.[key];
        return typeof value === 'number' && !isNaN(value) ? value : 0;
      };

      const totalExecutions = partnerTasks.reduce((sum, t) => sum + getMetric(t, 'totalExecutions'), 0);

      const activitySummary = {
        last24Hours: {
          filesReceived: inboundTasks.reduce((sum, t) => sum + getMetric(t, 'totalFilesProcessed'), 0),
          filesSent: outboundTasks.reduce((sum, t) => sum + getMetric(t, 'totalFilesProcessed'), 0),
          bytesTransferred: partnerTasks.reduce((sum, t) => sum + getMetric(t, 'totalBytesTransferred'), 0),
          successfulTransfers: partnerTasks.reduce((sum, t) => sum + getMetric(t, 'successfulExecutions'), 0),
          failedTransfers: partnerTasks.reduce((sum, t) => sum + getMetric(t, 'failedExecutions'), 0),
        },
        last30Days: {
          totalTransfers: totalExecutions,
          averageDaily: Math.round(totalExecutions / 30),
          peakDay: new Date().toISOString(),
          transfersByDirection: {
            inbound: inboundTasks.reduce((sum, t) => sum + getMetric(t, 'totalExecutions'), 0),
            outbound: outboundTasks.reduce((sum, t) => sum + getMetric(t, 'totalExecutions'), 0),
          },
        },
      };

      const lastActivity = partnerTasks.reduce((latest, task) => {
        if (task.lastExecution?.startTime) {
          const execTime = new Date(task.lastExecution.startTime);
          if (!latest || execTime > new Date(latest)) {
            return task.lastExecution.startTime;
          }
        }
        return latest;
      }, partner.lastActivity || null);

      return {
        ...partner,
        connections: partnerConnections,
        tasks: partnerTasks,
        status,
        lastActivity,
        activitySummary,
        taskCounts,
      };
    });
    // Defensive deps: tasks/allConnections/connectionStatuses/refreshKey trigger
    // recompute even though only accessed via the get*ByPartner closures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePartners, tasks, allConnections, getTasksByPartner, getConnectionsByPartner, connectionStatuses, refreshKey]);

  // THUB-298: derived from the URL, NOT stored in state.
  //
  // This used to be `useState` plus two effects plus a `userClosedModal` ref: one effect
  // re-synced the stored copy whenever `partners` changed (deep-comparing with JSON.stringify),
  // another seeded it from the URL on load, and the ref existed purely to stop the first effect
  // re-opening the modal after a close. All of that machinery existed to keep a duplicate of
  // data we can just compute -- the route already carries the selection (`/partners/:partnerId`).
  //
  // Deriving it makes the previously hand-rolled behaviours automatic:
  //   - a partner refresh flows straight into the open modal (was the JSON.stringify diff);
  //   - a deleted partner closes the modal (was the effect's else branch);
  //   - closing navigates to /partners, so there is no stale copy to race (was the ref).
  const selectedPartner: EnhancedPartner | null = partnerId
    ? partners.find(p => p.partnerId === partnerId) ?? null
    : null;

  const loading = partnersLoading || tasksLoading || connectionsLoading;
  const error = partnersError;



  // Filter, search, and sort data
  const filteredAndSortedData = useMemo(() => {
    let result = [...partners];

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(search) ||
        p.description?.toLowerCase().includes(search) ||
        p.status.toLowerCase().includes(search)
      );
    }

    if (filters.status.length > 0) {
      result = result.filter(p => {
        const provStatus = p.provisioningStatus || 'active';
        return filters.status.includes(provStatus as PartnerStatus);
      });
    }
    if (filters.isActive.length > 0) {
      result = result.filter(p => filters.isActive.includes(p.isActive));
    }
    if (filters.hasActiveTasks.length > 0) {
      const hasActiveTasks = filters.hasActiveTasks.includes(true);
      result = result.filter(p => hasActiveTasks ? p.taskCounts.enabled > 0 : p.taskCounts.enabled === 0);
    }

    result.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      if (sortField === 'taskCounts' as keyof EnhancedPartner) {
        aVal = a.taskCounts?.total || 0;
        bVal = b.taskCounts?.total || 0;
      }

      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return result;
  }, [partners, searchTerm, filters, sortField, sortOrder]);

  // Navigating IS the selection now -- selectedPartner is derived from the route above.
  const handlePartnerSelect = useCallback((partner: EnhancedPartner) => {
    navigate(`/partners/${partner.partnerId}`);
    onPartnerSelect?.(partner);
  }, [navigate, onPartnerSelect]);

  const handlePartnerUpdate = useCallback(async () => {
    await refreshPartners();
    setRefreshKey(prev => prev + 1);
  }, [refreshPartners]);

  const handleSort = (field: keyof EnhancedPartner) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = useCallback((field: keyof EnhancedPartner) => {
    if (sortField !== field) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  }, [sortField, sortOrder]);

  const clearFilters = () => {
    setFilters({
      status: [],
      isActive: [],
      hasActiveConnections: [],
      hasActiveTasks: []
    });
    setSearchTerm('');
  };

  const hasActiveFilters = searchTerm ||
    filters.status.length > 0 ||
    filters.isActive.length > 0 ||
    filters.hasActiveTasks.length > 0;

  // T053: Helper to get provisioning status from partner
  const getProvisioningStatus = (partner: EnhancedPartner): ProvisioningStatus => {
    return partner.provisioningStatus || 'active';
  };

  // Format relative time for last activity.
  //
  // THUB-186: this was a local copy carrying the exact THUB-272 bug. `new Date(isoDate)`
  // on a NAIVE backend timestamp (no offset) is parsed by ECMAScript as LOCAL time, which
  // future-dates it by the UTC offset — so diffMs went negative, `diffMins < 1` matched,
  // and every recent partner activity rendered "Just now". Live NP proves the input shape:
  // all 19 partner rows carry a naive `lastActivity`.
  //
  // formatRelativeTime/formatDateTime normalise naive values to UTC and handle the
  // future-timestamp case explicitly, so the fix is to stop hand-rolling this.
  const formatRelativeTime = (isoDate: string): { text: string; secondary: string } => {
    if (!isoDate) return { text: '—', secondary: '' };
    return {
      text: sharedRelativeTime(isoDate, timezone),
      secondary: formatDateTime(isoDate, timezone, 'full'),
    };
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-neutral-200 dark:border-slate-700 border-t-primary-500 rounded-full animate-spin" />
          <p className="text-neutral-600 dark:text-slate-400 font-medium">Loading partners...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center p-8 rounded-xl border border-danger-200 dark:border-danger-800 bg-danger-50 dark:bg-danger-900/20 max-w-md">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-danger-100 dark:bg-danger-900/40 flex items-center justify-center">
            <svg className="w-6 h-6 text-danger-600 dark:text-danger-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-danger-700 dark:text-danger-300 mb-2">
            Error Loading Partners
          </h3>
          <p className="text-danger-600 dark:text-danger-400 mb-4">{error}</p>
          <Button variant="primary" onClick={refreshPartners}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white">
            Partners
          </h2>
          <p className="text-sm text-neutral-500 dark:text-slate-400 mt-0.5">
            Manage partner integrations and monitor status
          </p>
        </div>
        <RoleGuard minimumRole="admin">
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Create Partner
          </Button>
        </RoleGuard>
      </div>

      {/* Controls Section */}
      <div className="flex flex-col md:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="w-4 h-4 text-neutral-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search partners..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            className="
              w-full pl-9 pr-9 py-2
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              rounded-lg
              text-sm text-neutral-900 dark:text-white
              placeholder-neutral-400 dark:placeholder-slate-500
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              transition-all duration-200
            "
          />
          {searchTerm && (
            <button
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-neutral-400 hover:text-neutral-600 dark:hover:text-slate-300"
              onClick={() => {
                setSearchTerm('');
              }}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={filters.status.length === 1 ? filters.status[0] : ''}
            onChange={(e) => {
              const value = e.target.value;
              setFilters(prev => ({
                ...prev,
                status: value ? [value as PartnerStatus] : []
              }));
            }}
            className="
              px-3 py-2 min-w-[130px]
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              rounded-lg
              text-sm text-neutral-900 dark:text-white
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              transition-all duration-200
            "
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="provisioning">Provisioning</option>
            <option value="failed">Failed</option>
            <option value="deleting">Deleting</option>
          </select>

          <select
            value={filters.isActive.length === 1 ? filters.isActive[0].toString() : ''}
            onChange={(e) => {
              const value = e.target.value;
              setFilters(prev => ({
                ...prev,
                isActive: value ? [value === 'true'] : []
              }));
            }}
            className="
              px-3 py-2 min-w-[130px]
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              rounded-lg
              text-sm text-neutral-900 dark:text-white
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              transition-all duration-200
            "
          >
            <option value="">All Partners</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>

          {hasActiveFilters && (
            <button
              className="
                px-3 py-1.5
                text-xs font-medium
                text-neutral-500 dark:text-slate-400
                hover:text-neutral-900 dark:hover:text-white
                hover:bg-neutral-100 dark:hover:bg-slate-800
                rounded-lg
                transition-colors duration-200
              "
              onClick={clearFilters}
            >
              Clear
            </button>
          )}

          {hasActiveFilters && (
            <span className="text-xs text-neutral-400 dark:text-slate-500 ml-1">
              {filteredAndSortedData.length} of {partners.length}
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-neutral-200 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-neutral-50 dark:bg-slate-800/50 border-b border-neutral-200 dark:border-slate-700">
                <th
                  className="px-5 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-slate-200 transition-colors"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1.5">
                    Partner
                    <span className="text-neutral-300 dark:text-slate-600">{getSortIcon('name')}</span>
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-slate-200 transition-colors"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1.5">
                    Status
                    <span className="text-neutral-300 dark:text-slate-600">{getSortIcon('status')}</span>
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider"
                >
                  Connections
                </th>
                {showTaskCounts && (
                  <th
                    className="px-4 py-3 text-center text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-slate-200 transition-colors"
                    onClick={() => handleSort('taskCounts' as keyof EnhancedPartner)}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      Tasks
                      <span className="text-neutral-300 dark:text-slate-600">{getSortIcon('taskCounts' as keyof EnhancedPartner)}</span>
                    </div>
                  </th>
                )}
                <th
                  className="px-4 py-3 text-right text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-slate-200 transition-colors"
                  onClick={() => handleSort('lastActivity')}
                >
                  <div className="flex items-center justify-end gap-1.5">
                    Last Activity
                    <span className="text-neutral-300 dark:text-slate-600">{getSortIcon('lastActivity')}</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-slate-800">
              {filteredAndSortedData.length === 0 ? (
                <tr>
                  <td colSpan={showTaskCounts ? 5 : 4} className="px-5 py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-10 h-10 text-neutral-300 dark:text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                      <p className="text-sm text-neutral-500 dark:text-slate-400">
                        {hasActiveFilters ? 'No partners match your filters' : 'No partners found'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAndSortedData.map((partner) => {
                  const activity = formatRelativeTime(partner.lastActivity || '');
                  const connectionCount = partner.connections?.length || 0;
                  return (
                    <tr
                      key={partner.partnerId}
                      onClick={() => handlePartnerSelect(partner)}
                      className="
                        group
                        cursor-pointer
                        hover:bg-primary-50/50 dark:hover:bg-primary-900/5
                        transition-colors duration-150
                      "
                    >
                      {/* Partner Name */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="
                            w-9 h-9 rounded-lg flex-shrink-0
                            bg-gradient-to-br from-primary-500 to-primary-700
                            flex items-center justify-center
                            text-white font-semibold text-xs
                            shadow-sm
                          ">
                            {partner.name.substring(0, 2).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-neutral-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors truncate">
                              {partner.name}
                            </div>
                            {partner.description && (
                              <div className="text-xs text-neutral-400 dark:text-slate-500 truncate max-w-[200px]">
                                {partner.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3.5">
                        <ResourceStatusBadge
                          status={getProvisioningStatus(partner)}
                          size="sm"
                        />
                      </td>

                      {/* Connections */}
                      <td className="px-4 py-3.5 text-center">
                        <span className={`
                          inline-flex items-center justify-center
                          min-w-[24px] h-6 px-1.5
                          text-xs font-medium rounded-md
                          ${connectionCount > 0
                            ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                            : 'bg-neutral-100 text-neutral-400 dark:bg-slate-800 dark:text-slate-500'
                          }
                        `}>
                          {connectionCount}
                        </span>
                      </td>

                      {/* Tasks */}
                      {showTaskCounts && (
                        <td className="px-4 py-3.5 text-center">
                          <div className="inline-flex items-center gap-1.5">
                            <span className={`
                              inline-flex items-center justify-center
                              min-w-[24px] h-6 px-1.5
                              text-xs font-medium rounded-md
                              ${partner.taskCounts.total > 0
                                ? 'bg-neutral-100 text-neutral-700 dark:bg-slate-800 dark:text-slate-300'
                                : 'bg-neutral-100 text-neutral-400 dark:bg-slate-800 dark:text-slate-500'
                              }
                            `}>
                              {partner.taskCounts.total}
                            </span>
                            {partner.taskCounts.enabled > 0 && (
                              <span className="text-[11px] text-success-600 dark:text-success-400 font-medium">
                                {partner.taskCounts.enabled} on
                              </span>
                            )}
                            {partner.taskCounts.running > 0 && (
                              <span className="flex items-center gap-0.5 text-[11px] text-warning-600 dark:text-warning-400 font-medium">
                                <span className="w-1.5 h-1.5 rounded-full bg-warning-500 animate-pulse" />
                                {partner.taskCounts.running}
                              </span>
                            )}
                          </div>
                        </td>
                      )}

                      {/* Last Activity */}
                      <td className="px-4 py-3.5 text-right">
                        <div className="text-sm text-neutral-700 dark:text-slate-300">
                          {activity.text}
                        </div>
                        {activity.secondary && (
                          <div className="text-[11px] text-neutral-400 dark:text-slate-500">
                            {activity.secondary}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {filteredAndSortedData.length > 0 && (
          <div className="
            px-5 py-3
            bg-neutral-50/50 dark:bg-slate-800/30
            border-t border-neutral-200 dark:border-slate-700
          ">
            <div className="text-xs text-neutral-500 dark:text-slate-400">
              {hasActiveFilters
                ? `Showing ${filteredAndSortedData.length} of ${partners.length} partners`
                : `Showing ${filteredAndSortedData.length} partners`
              }
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedPartner && (
        <PartnerDetails
          partner={selectedPartner}
          onClose={() => {
            navigate('/partners');
            refreshPartners();
          }}
          onUpdate={handlePartnerUpdate}
        />
      )}

      {showCreateModal && (
        <CreatePartner
          onSuccess={() => {
            setShowCreateModal(false);
            refreshPartners();
          }}
          onCancel={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
}

export default PartnerList;
